import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { HomeIcon, UsersIcon, BriefcaseIcon, FileTextIcon, CalculatorIcon, ScaleIcon, StampIcon, MessageSquareIcon, ChevronRightIcon } from 'lucide-react';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.8,
    ease: 'easeOut'
  }
};
const staggerContainer = {
  initial: {
    opacity: 0
  },
  whileInView: {
    opacity: 1
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    staggerChildren: 0.15
  }
};
const services = [{
  id: 'conveyancing',
  title: 'Conveyancing & Property Law',
  icon: HomeIcon,
  image: 'https://cdn.magicpatterns.com/uploads/95hdmJPwwsw14MzoD8zhsZ/Land_transfer.jpg',
  desc: 'Property transfers, title deeds, mortgage registrations, sectional titles and conveyancing services from start to finish.',
  link: '/conveyancing'
}, {
  id: 'family-law',
  title: 'Family Law',
  icon: UsersIcon,
  image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80&w=800',
  desc: 'Divorce, child maintenance, custody, guardianship and family legal matters handled with sensitivity and professionalism.',
  link: '/contact'
}, {
  id: 'corporate-law',
  title: 'Corporate & Commercial Law',
  icon: BriefcaseIcon,
  image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&q=80&w=800',
  desc: 'Commercial contracts, company law, legal compliance and business advisory services for enterprises of all sizes.',
  link: '/contact'
}, {
  id: 'labour-law',
  title: 'Employment & Labour Law',
  icon: FileTextIcon,
  image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&q=80&w=800',
  desc: 'Employment disputes, dismissals, disciplinary hearings and comprehensive labour advice for employers and employees.',
  link: '/contact'
}, {
  id: 'financial-recoveries',
  title: 'Financial Recoveries',
  icon: CalculatorIcon,
  image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&q=80&w=800',
  desc: 'Debt recovery, financial litigation and commercial recoveries executed efficiently to protect your bottom line.',
  link: '/contact'
}, {
  id: 'civil-litigation',
  title: 'Civil Litigation',
  icon: ScaleIcon,
  image: 'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?auto=format&fit=crop&q=80&w=800',
  desc: 'Professional court representation and civil dispute resolution across various jurisdictions in Botswana.',
  link: '/contact'
}, {
  id: 'notarial-services',
  title: 'Notarial Services',
  icon: StampIcon,
  image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80&w=800',
  desc: 'Affidavits, authentication of documents, powers of attorney and registration of notarial bonds.',
  link: '/contact'
}, {
  id: 'legal-consultation',
  title: 'Legal Consultation',
  icon: MessageSquareIcon,
  image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80&w=800',
  desc: 'Professional legal advice tailored to individuals and businesses seeking clarity on complex legal issues.',
  link: '/contact'
}];
export function PracticeAreas() {
  return <div className="w-full pt-24 bg-brand-offwhite" data-id="element-373">
      {/* Page Header */}
      <section className="relative py-20 bg-brand-charcoal text-white overflow-hidden" data-id="element-374">
        <div className="absolute inset-0 z-0 opacity-20" data-id="element-375">
          <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&q=80&w=2000" alt="Practice Areas background" className="w-full h-full object-cover" data-id="element-376" />
        </div>
        <div className="relative z-10 max-w-container mx-auto px-6 text-center" data-id="element-377">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="font-serif text-5xl md:text-6xl font-bold mb-6" data-id="element-378">
            Practice Areas
          </motion.h1>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-xl text-gray-300 max-w-2xl mx-auto" data-id="element-379">
            Comprehensive legal expertise tailored to protect your interests and
            achieve your objectives.
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24" data-id="element-380">
        <div className="max-w-container mx-auto px-6" data-id="element-381">
          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-id="element-382">
            {services.map(service => <motion.div key={service.id} variants={fadeInUp} className="group rounded-2xl overflow-hidden shadow-soft hover:shadow-card transition-all duration-300 bg-white border border-gray-100 flex flex-col h-full" data-id="element-383">
                <div className="relative h-48 overflow-hidden shrink-0" data-id="element-384">
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal/80 to-transparent z-10" data-id="element-385"></div>
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" data-id="element-386" />
                  <div className="absolute bottom-4 left-4 z-20 text-white flex items-center" data-id="element-387">
                    <service.icon className="w-6 h-6 mr-3 text-brand-gold" data-id="element-388" />
                    <h3 className="font-serif text-xl font-bold leading-tight drop-shadow-md" data-id="element-389">
                      {service.title}
                    </h3>
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-grow" data-id="element-390">
                  <p className="text-gray-600 mb-6 flex-grow text-sm leading-relaxed" data-id="element-391">
                    {service.desc}
                  </p>
                  <Link to={service.link} className="inline-flex items-center justify-center w-full py-3 px-4 bg-brand-offwhite hover:bg-brand-green hover:text-white text-brand-green rounded-xl font-medium transition-colors group/btn" data-id="element-392">
                    Learn More
                    <ChevronRightIcon className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" data-id="element-393" />
                  </Link>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-brand-green text-center px-6" data-id="element-394">
        <motion.div {...fadeInUp} className="max-w-3xl mx-auto" data-id="element-395">
          <h2 className="font-serif text-4xl font-bold text-white mb-6" data-id="element-396">
            Not sure which service you need?
          </h2>
          <p className="text-lg text-brand-offwhite/90 mb-10" data-id="element-397">
            Schedule a consultation with one of our attorneys to discuss your
            specific situation and determine the best legal approach.
          </p>
          <Link to="/contact" className="inline-block px-8 py-4 bg-brand-gold hover:bg-brand-gold-light text-white rounded-full font-medium transition-all shadow-lg" data-id="element-398">
            Book a Consultation
          </Link>
        </motion.div>
      </section>
    </div>;
}